/**
 * Create a DocumentFragment from a HTML string, or can be used as a tag
 * function with a template literal.
 *
 * @param {string|TemplateStringsArray} html
 * @returns {DocumentFragment}
 */
export function createFragment(html) {
  const template = document.createElement("template");
  template.innerHTML = html;
  return template.content;
}

/**
 * Create a tag function that creates a fragment from a template literal by
 * using expressions as keys to get values from a data object.
 *
 * For example:
 * ```js
 * createFragmentWithData({foo: 123})`
 *    <div>${"foo"}</div>
 * `;
 * ```
 * returns
 * ```html
 * <div>123</div>
 * ```
 *
 * @param {object} data
 * @returns {(html: string[], keys: object) => DocumentFragment}
 */
export function createFragmentWithData(data) {
  /**
   * @param {string[]} html
   * @param {object} keys
   */
  return (html, ...keys) => {
    const content = html.reduce(
      (previous, current, index) =>
        `${previous}${current}${data[keys[index]] ?? ""}`,
      "",
    );
    return createFragment(content);
  };
}

/**
 * Create one HTML element from a HTML string.
 *
 * @param {string} html
 * @returns {HTMLElement}
 */
export function createElement(html) {
  return createFragment(html).firstElementChild;
}

/**
 * Create many HTML elements from a HTML string.
 *
 * @param {string} html
 * @returns {HTMLCollection}
 */
export function createElements(html) {
  return createFragment(html).children;
}

/**
 * Find a child element by selector, throws an error if not exactly one.
 *
 * @param {Document|HTMLElement} root
 * @param {string} selector
 * @returns {HTMLElement}
 */
export function getElement(root, selector) {
  const elements = getElements(root, selector);
  if (elements.length > 1) {
    throw new Error(
      `Expected 1 element for ${selector} in ${root instanceof Document ? "document" : root.tagName.toLowerCase()}, found ${elements.length}`,
    );
  } else if (elements.length === 0) {
    throw new Error(
      `No element found for ${selector} in ${root instanceof Document ? "document" : root.tagName.toLowerCase()}`,
    );
  }
  return elements[0];
}

/**
 * Find a child element by selector, returns the first if more than one.
 *
 * @param {Document|HTMLElement} root
 * @param {string} selector
 * @returns {HTMLElement|null}
 */
export function getOptionalElement(root, selector) {
  return getElements(root, selector)[0] ?? null;
}

/**
 * Find child elements by selector.
 *
 * @param {Document|HTMLElement} root
 * @param {string} selector
 * @returns {HTMLElement[]}
 */
export function getElements(root, selector) {
  return "shadowRoot" in root && root.shadowRoot
    ? /** @type HTMLElement[] */ ([
        ...root.shadowRoot.querySelectorAll(selector),
      ])
    : /** @type HTMLElement[] */ ([...root.querySelectorAll(selector)]);
}
